import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getDepartment, listDepartments, type DepartmentListItem } from "@/api";
import { onResync, onSocketEvent } from "@/socket";
import { errorMessage } from "@/shared/utils/errors";
import { departmentsGranting, holds, type HeldByDepartment } from "@/shared/utils/spaceLocation";

/**
 * How long what the caller holds in a department is trusted. A department's roles are edited in its
 * own room, which only its page joins, so an edit to the caller's role reaches no other session —
 * a dialog that picks a location asks again once this has passed, rather than offer a department
 * the server would now refuse.
 */
export const HELD_TTL_MS = 30_000;

/**
 * The user-room events that change the caller's place in a department: the list is re-read, and
 * what is held in each with it. (`space:access:changed` moves which spaces they reach, not which
 * departments they are in, so it is not here.)
 */
export const MY_DEPARTMENT_EVENTS = ["department:membership:changed"] as const;

/**
 * The departments of the signed-in user, and what they may do in each — for every picker that puts
 * a project or a dataspace somewhere: the Designer's create-project dialog, the Operator's
 * create-dataspace dialog, and the move dialog both settings pages open.
 *
 * Two reads. The list (`GET /departments`) is cheap and says where the caller is. What they hold
 * there (`GET /departments/{id}`, once per department) is only needed by a dialog that is open, so
 * it is read when one asks (`loadHeld`) and then trusted for `HELD_TTL_MS`; a membership event drops
 * it. Nothing is read until somebody asks, and only what was read is re-read.
 *
 * Kept apart from the department pages' own store, which also holds a list: that one is tied to the
 * page that owns the department's room, and these pickers have no such page.
 */
export const useMyDepartmentsStore = defineStore("myDepartments", () => {
	const list = ref<DepartmentListItem[] | null>(null);
	const held = ref<Record<string, string[]>>({});
	const loading = ref(false);
	const error = ref("");

	/** When `held` was read, and by which generation of the list — see `loadHeld`. */
	let heldAt = 0;
	let generation = 0;
	let listing: Promise<void> | null = null;
	let holding: Promise<void> | null = null;

	/** The departments, read once and kept: the list a membership event re-reads. */
	function load(): Promise<void> {
		if (list.value !== null) return Promise.resolve();
		return reloadList();
	}

	function reloadList(): Promise<void> {
		if (listing) return listing;
		const mine = generation;
		loading.value = true;
		listing = (async () => {
			try {
				const fetched = await listDepartments();
				if (mine === generation) {
					list.value = fetched;
					error.value = "";
				}
			} catch (e) {
				if (mine === generation) error.value = errorMessage(e);
			} finally {
				// An older read, overtaken by `refresh`, leaves the newer one's state alone.
				if (mine === generation) {
					loading.value = false;
					listing = null;
				}
			}
		})();
		return listing;
	}

	/**
	 * What the caller holds in each of their departments. A department that cannot be read (it was
	 * deleted, or they were just removed) holds nothing: deny is the safe answer for a picker.
	 */
	function loadHeld(): Promise<void> {
		if (holding) return holding;
		const fresh = list.value !== null && heldAt > 0 && Date.now() - heldAt < HELD_TTL_MS;
		if (fresh) return Promise.resolve();
		const mine = generation;
		holding = (async () => {
			try {
				await load();
				const departments = list.value ?? [];
				const answers = await Promise.all(
					departments.map((d) =>
						getDepartment(d.id)
							.then((r) => [d.id, r.effective_entitlements] as const)
							.catch(() => [d.id, [] as string[]] as const),
					),
				);
				if (mine !== generation) return;
				held.value = Object.fromEntries(answers);
				heldAt = Date.now();
			} finally {
				if (mine === generation) holding = null;
			}
		})();
		return holding;
	}

	/**
	 * Drops what is known, and reads again what had been read: the caller's place in a department
	 * changed. A read already in flight is for the old answer and is not applied.
	 */
	function refresh(): Promise<void> {
		// Asked for, or being asked for right now: either way somebody is waiting on the answer.
		const hadList = list.value !== null || listing !== null;
		const hadHeld = heldAt > 0 || holding !== null;
		generation++;
		listing = null;
		holding = null;
		heldAt = 0;
		if (!hadList) return Promise.resolve();
		return reloadList().then(() => (hadHeld ? loadHeld() : undefined));
	}

	/** An event of the user's room, or `resync` of it: re-reads what the caller's place changed. */
	function applyRemote(event: string): Promise<void> {
		return (MY_DEPARTMENT_EVENTS as readonly string[]).includes(event)
			? refresh()
			: Promise.resolve();
	}

	const departments = computed(() => list.value ?? []);
	const heldBy = computed<HeldByDepartment>(() => held.value);

	return {
		list,
		departments,
		held: heldBy,
		loading,
		error,
		load,
		loadHeld,
		refresh,
		applyRemote,
		can: (departmentId: string, entitlement: string) =>
			holds(held.value, departmentId, entitlement),
		granting: (entitlement: string) =>
			departmentsGranting(list.value ?? [], held.value, entitlement),
	};
});

/**
 * Subscribes the store to the user's room, once per app: `onSocketEvent` handlers live as long as
 * the page does, and the store re-reads only what had been read, so a session that never opens a
 * picker pays nothing. Called by `useMyDepartments`.
 */
const wired = new WeakSet<object>();
export function wireMyDepartments(store: ReturnType<typeof useMyDepartmentsStore>): void {
	if (wired.has(store)) return;
	wired.add(store);
	for (const event of MY_DEPARTMENT_EVENTS) {
		onSocketEvent(event, () => void store.applyRemote(event));
	}
	onResync((room) => {
		if (room.startsWith("user:")) void store.refresh();
	});
}
